import { LoadingRows } from "./Loading";
import { runListRefresh } from "./workspace-queries";
import { useMemo, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { bootRequest } from "./boot-requests";
import type { SavedRun } from "../../../packages/contracts/src/workspace";
import { ArrowClockwiseIcon, MagnifyingGlassIcon } from "@phosphor-icons/react";
import { api } from "./api";
import { useDashboard } from "./Dashboard";
import { Button } from "./components/ui/button";
import { RunsTable, runSearchText, type RunMethod } from "./components/dashboard/RunsPanel";

export type ListSearch = { method?: string; status?: string; q?: string };
export function listSearch(search: Record<string, unknown>): ListSearch {
  return Object.fromEntries(["method", "status", "q"].flatMap(key => typeof search[key] === "string" && search[key] ? [[key, (search[key] as string).slice(0, 200)]] : []));
}
function RefreshList({ label, refresh }: { label: string; refresh: () => Promise<unknown> }) {
  const [refreshing, setRefreshing] = useState(false);
  return <Button variant="ghost" size="icon" className="ml-auto" aria-label={label} disabled={refreshing} onClick={async () => {
    setRefreshing(true);
    try { await refresh(); } catch { /* The query renders its error beside the list. */ } finally { setRefreshing(false); }
  }}><ArrowClockwiseIcon size={17} className={refreshing ? "spin" : undefined} /></Button>;
}
function EmptyList({ loading, message, filtered, clear }: { loading: boolean; message: string; filtered: boolean; clear: () => void }) {
  return <div className="dash-state" role="status"><p>{loading ? "Loading…" : message}</p>{!loading && filtered && <Button variant="secondary" onClick={clear}>Clear filters</Button>}</div>;
}
/** status=attention shows runs that need attention or failed, status=running shows queued and running runs. */
function statusMatches(filter: string | undefined, run: SavedRun) {
  if (!filter) return true;
  if (filter === "attention") return run.status === "failed";
  if (filter === "running") return !!run.pending || run.status === "running" || run.status === "queued";
  return run.status === filter;
}
export function RunListPage() {
  const { workflows, workflowsLoading, accountScope } = useDashboard();
  const search = useSearch({ strict: false }) as ListSearch;
  const navigate = useNavigate();
  const client = useQueryClient();
  const runs = useQuery({
    queryKey: ["runs", "list", accountScope], queryFn: () => bootRequest("runs:list", () => api.runs()), refetchInterval: runListRefresh,
    // The sidebar already has the latest runs. Show them until the full list arrives.
    placeholderData: () => client.getQueryData<SavedRun[]>(["runs", "overview", accountScope]),
  });
  const loading = runs.isPending || runs.isPlaceholderData;
  const update = (change: ListSearch) => void navigate({ to: "/runs", search: { ...search, ...change }, replace: true });
  const methods = useMemo(() => new Map<string, RunMethod>(workflows.map(method => {
    // The methods list carries the workflow record, so the live version id is there.
    const liveVersionId = (method as { current_version_id?: string | null }).current_version_id ?? null;
    const known = (runs.data ?? []).filter(run => run.workflow_id === method.id);
    const liveNumber = known.find(run => run.workflow_version_id === liveVersionId)?.version_number ?? (known.length ? Math.max(...known.map(run => run.version_number)) : null);
    return [method.id, { id: method.id, name: method.name, liveVersionId, liveNumber, overview: method.overview ?? null }];
  })), [workflows, runs.data]);
  const query = (search.q ?? "").toLowerCase();
  const found = (runs.data ?? []).filter(run => (!search.method || run.workflow_id === search.method) && runSearchText(run).includes(query));
  const rows = found.filter(run => statusMatches(search.status, run));
  const attention = found.filter(run => statusMatches("attention", run)).length, running = found.filter(run => statusMatches("running", run)).length;
  const filtered = !!(search.method || search.status || search.q);
  return <section className="dashboard-workflows workspace-list" aria-label="Runs">
    <div className="list-controls">
      <label className="list-search"><MagnifyingGlassIcon size={16} /><input aria-label="Find a run" placeholder="Find a run by name, input or result…" value={search.q ?? ""} onChange={e => update({ q: e.target.value || undefined })} /></label>
      <div className="p-seg" role="group" aria-label="Show runs">
        <button type="button" aria-pressed={!search.status} onClick={() => update({ status: undefined })}>All</button>
        <button type="button" aria-pressed={search.status === "attention"} onClick={() => update({ status: "attention" })}>Failed{attention > 0 && <b>{attention}</b>}</button>
        <button type="button" aria-pressed={search.status === "running"} onClick={() => update({ status: "running" })}>Running{running > 0 && <b>{running}</b>}</button>
      </div>
      <select disabled={workflowsLoading} aria-label="Filter runs by method" value={search.method ?? ""} onChange={e => update({ method: e.target.value || undefined })}>
        <option value="">All methods</option>{workflows.map(method => <option key={method.id} value={method.id}>{method.name}</option>)}
      </select>
      <RefreshList label="Refresh runs" refresh={() => runs.refetch()} />
    </div>
    {runs.error && <p role="alert">{runs.error.message}</p>}
    {rows.length || loading ? <RunsTable runs={rows} methods={methods} busy={runs.isPlaceholderData}>
      {runs.isPending || (runs.isPlaceholderData && !rows.length) ? <LoadingRows columns={6} /> : undefined}
    </RunsTable> : !runs.error && <EmptyList loading={false} message={filtered ? "No matching runs" : "No runs have been recorded yet."} filtered={filtered} clear={() => void navigate({ to: "/runs", search: {}, replace: true })} />}
  </section>;
}
